import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { I18nModule } from '@spartacus/core';
import { Observable, Subscription, distinctUntilChanged, filter, map, skip, switchMap, take, tap } from 'rxjs';
import { CheckoutFormService } from 'src/app/core/checkout/facade/checkout-form.service';
import { PaymentMode } from 'src/app/model/checkout.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { PaymentService } from 'src/app/service/payment/payment.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';

@Component({
  selector: 'app-checkout-payment-mode-selector',
  standalone: true,
  imports: [CommonModule, I18nModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './checkout-payment-mode-selector.component.html',
  styleUrl: './checkout-payment-mode-selector.component.scss',
  providers: [CheckoutFormService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutPaymentModeSelectorComponent implements OnInit, OnDestroy {
  paymentModes: PaymentMode[];
  paymentModeForm: FormGroup;
  paymentTerms: string;
  businessType: string;

  // Subscriptions
  paymentModeFormSubscription$: Subscription;
  orderTypeValueChangeSubscription$: Subscription;

  get paymentModeControl() {
    return this.paymentModeForm.get('paymentMode');
  }

  constructor(
    protected checkoutFormService: CheckoutFormService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private paymentService: PaymentService,
    private currentBusinesstype: CurrentBusinessType,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  ngOnInit(): void {
    this.buildForm();
    this.getPaymentModes().subscribe();
    if (this.businessType === 'fish') {
      this.orderTypeValueChangeSubscription$ = this.orderTypeValueChanges()
        .pipe(
          skip(1),
          switchMap(() => this.checkoutServiceExtends.isQueryOrderTypeStable.pipe(filter(Boolean), take(1))),
          switchMap(() => this.getPaymentModes()) // call paymentModes API each time order type changes and orderType is stable
        )
        .subscribe();
    }
  }

  buildForm(): void {
    this.paymentModeForm = this.formBuilder.group({
      paymentMode: [null, Validators.required],
    });
    this.updateCheckoutForm();
    this.paymentModeFormSubscription$ = this.paymentModeForm.valueChanges.subscribe(() => {
      this.updateCheckoutForm();
    });
  }

  getPaymentModes(): Observable<any> {
    return this.paymentService.getPaymentModes().pipe(tap((res) => this.setDefault(res)));
  }

  orderTypeValueChanges(): Observable<any> {
    return this.checkoutFormService.checkoutForms.pipe(
      map((data) => data.formsObjectValue.orderType),
      distinctUntilChanged(),
      filter(Boolean)
    );
  }

  onSelectPayment(): void {
    this.updatePaymentTerms(this.paymentModeControl.value.code);
  }

  updatePaymentTerms(selectedModeCode: string): void {
    const selectedMode: any = this.paymentModes.find((mode: PaymentMode) => mode.code === selectedModeCode);
    this.paymentTerms = selectedMode.paymentTerms;
  }

  updateCheckoutForm(): void {
    this.checkoutFormService.updateCheckoutForm(this.paymentModeForm.value);
    this.checkoutServiceExtends.setCheckoutFormsError('CHECKOUT_PAYMENT_DETAILS', !this.paymentModeForm.valid);
  }

  setDefault(paymentModes: any): void {
    this.paymentModes = paymentModes;
    this.paymentModeControl.setValue(this.paymentModes[0]);
    this.updatePaymentTerms(this.paymentModes[0].code);
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.paymentModeFormSubscription$?.unsubscribe();
    this.orderTypeValueChangeSubscription$?.unsubscribe();
  }
}
